import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnhancedTab } from '@swyftx/aviary/molecules/EnhancedTabs/EnhancedTabs.types';
import { negativeTabValues, positiveTabValues } from '@swyftx/margin/build/consts';
import { MarginOrderDirection, MarginOrderType } from '@swyftx/margin/build/types';
import { MarginInstrument } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginTradePriceError } from 'src/lib/margin/hooks/trade';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

type Props = {
  tradeType: MarginOrderType;
  tradeSide: MarginOrderDirection;
  price: string;
  market: MarginInstrument;
  isOpenPosition?: boolean;
  onChange: (price: string) => void;
};

const useMarginPriceInput = ({ tradeType, tradeSide, price, market, isOpenPosition, onChange }: Props) => {
  const { getMarketPriceByInstrument } = useMarginPrices();
  const { getAssetByCode } = useMarkets();
  const asset = getAssetByCode('USD');

  const { error } = useMarginTradePriceError({
    price,
    tradeSide,
    tradeType,
    selectedMarketPrice: getMarketPriceByInstrument(market),
    selectedMarket: market,
    isOpenPosition,
  });
  const { t } = useTranslation('margin');

  const { formattedAmount, onChangeValue, priceScale } = useFormatInputText({
    asset,
    value: price,
    onChange,
    blockAlphaNumericChars: true,
    priceScaleOverride: market?.precision,
  });

  const inputLabel = useMemo(() => {
    if (isOpenPosition) return 'Avg fill price';

    switch (tradeType) {
      case MarginOrderType.Market:
        return t('tradePrice.market');
      case MarginOrderType.Limit:
        return t('tradePrice.limit');
      case MarginOrderType.Stop:
        return t('tradePrice.stop');
    }
  }, [isOpenPosition, t, tradeType]);

  const placeholder = useMemo(() => {
    const selectedMarketPrice = getMarketPriceByInstrument(market);

    switch (tradeSide) {
      case MarginOrderDirection.Long:
        return formatCurrency(selectedMarketPrice?.ask, undefined, { priceScale: market.precision });
      case MarginOrderDirection.Short:
        return formatCurrency(selectedMarketPrice?.bid, undefined, { priceScale: market.precision });
      default:
        tradeSide satisfies never;
    }
  }, [getMarketPriceByInstrument, market, tradeSide]);

  const tabs = useMemo((): EnhancedTab<string>[] => {
    if (
      (tradeType === MarginOrderType.Limit && tradeSide === MarginOrderDirection.Long) ||
      (tradeType === MarginOrderType.Stop && tradeSide === MarginOrderDirection.Short)
    ) {
      return negativeTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'error',
      }));
    } else {
      return positiveTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'success',
      }));
    }
  }, [tradeSide, tradeType]);

  const marketPrice = useMemo(() => {
    const selectedMarketPrice = getMarketPriceByInstrument(market);

    if (tradeSide === MarginOrderDirection.Long) {
      return selectedMarketPrice?.ask || '';
    } else {
      return selectedMarketPrice?.bid || '';
    }
  }, [getMarketPriceByInstrument, market, tradeSide]);

  const onChangeTab = useCallback(
    (newValue: string) => {
      const bigValue = Big(Big(price).eq(0) ? marketPrice : price)
        .times(Big(1).plus(newValue))
        .toFixed(priceScale);

      onChange(bigValue.toString());
    },
    [marketPrice, onChange, price, priceScale],
  );

  return {
    asset,
    placeholder,
    inputLabel,
    tabs,
    error,
    onChangeTab,
    formattedAmount,
    onChangeValue,
  };
};

export { useMarginPriceInput };
